import React, { useState } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';

const ExportButton = ({ 
  exportType, // 'orders' | 'products' | 'revenue' | 'documents' | 'customers'
  label = 'Export',
  filters = {}, // Additional query parameters
  variant = 'outline',
  size = 'default'
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Build query string from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const queryString = queryParams.toString();
      const url = `/export/${exportType}${queryString ? `?${queryString}` : ''}`;
      
      const response = await axiosInstance.get(url, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${exportType}_export.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Export erfolgreich heruntergeladen');
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Export');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleExport}
      disabled={isExporting}
    >
      {isExporting ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <FileSpreadsheet className="h-4 w-4 mr-2" />
      )}
      {label}
    </Button>
  );
};

// Multi-export dropdown for pages with multiple export options
const ExportDropdown = ({ 
  options = [], // Array of { type, label, filters }
  buttonLabel = 'Excel Export',
  variant = 'outline',
  size = 'default'
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [activeExport, setActiveExport] = useState(null);

  const handleExport = async (option) => {
    setIsExporting(true);
    setActiveExport(option.type);
    
    try {
      const queryParams = new URLSearchParams();
      if (option.filters) {
        Object.entries(option.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value);
          }
        });
      }
      
      const queryString = queryParams.toString();
      const url = `/export/${option.type}${queryString ? `?${queryString}` : ''}`;
      
      const response = await axiosInstance.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${option.type}_export.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${option.label} exportiert`);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Export');
    } finally {
      setIsExporting(false);
      setActiveExport(null);
    }
  };

  if (options.length === 1) {
    return (
      <ExportButton 
        exportType={options[0].type}
        label={buttonLabel}
        filters={options[0].filters}
        variant={variant}
        size={size}
      />
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={isExporting}>
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {buttonLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((option, index) => (
          <DropdownMenuItem
            key={index}
            onClick={() => handleExport(option)}
            disabled={isExporting}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            {option.label}
            {activeExport === option.type && (
              <Loader2 className="h-4 w-4 ml-2 animate-spin" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { ExportButton, ExportDropdown };
export default ExportButton;
